import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

/**
 * Staff-only Clu chat review: every Clu thread across orgs (who, how many
 * tool calls, credits, cost) with the reviewer's score on each one. Chats are
 * scored every 15 minutes and the summary posts daily. The server gates it
 * to staff (403 unless the Clerk email ends with @cleanlist.ai). Read-only.
 *
 * Timestamps are ISO-8601 UTC strings. Endpoints live under
 * `/api/v1/management/clu-chats`.
 */

export type CluChatOutcome =
  | 'succeeded'
  | 'partial'
  | 'failed'
  | 'abandoned'
  | 'no_ask';

/** What the outcome filter accepts: a reviewed outcome, or chats with no review yet. */
export type CluChatOutcomeFilter = CluChatOutcome | 'unreviewed';

export type CluChatSurface = 'portal' | 'slack' | 'agent';

export type CluChatBillingMode = 'shadow' | 'charged' | 'waived' | 'mixed';

export type CluChatBlockerCategory =
  | 'none'
  | 'missing_capability'
  | 'wrong_tool'
  | 'tool_error'
  | 'plan_or_credits_gate'
  | 'unclear_request'
  | 'misunderstood'
  | 'too_many_turns'
  | 'bad_answer'
  | 'other';

export interface CluChatReview {
  outcome: CluChatOutcome;
  /** 1 to 5. */
  score: number;
  user_goal: string;
  blocker_category: CluChatBlockerCategory;
  blocker_detail: string;
  better_response: string;
  product_gap: string | null;
  reviewed_at: string;
  reviewed_message_count: number;
  model_id: string;
}

export interface CluChatRow {
  conversation_id: string;
  title: string | null;
  organization_id: string;
  organization_name: string | null;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  surface: CluChatSurface;
  context_type: string;
  created_at: string;
  last_message_at: string;
  user_turns: number;
  message_count: number;
  tool_calls: number;
  tool_failures: number;
  credits: number;
  billing_mode: CluChatBillingMode | null;
  cost_usd: number;
  is_internal: boolean;
  /** Null until the reviewer has scored the chat. */
  review: CluChatReview | null;
}

export interface CluChatList {
  chats: CluChatRow[];
  total: number;
}

export interface CluChatBlockerCount {
  category: string;
  count: number;
}

export interface CluChatSummary {
  window_hours: number;
  chats: number;
  orgs: number;
  users: number;
  user_turns: number;
  tool_calls: number;
  tool_failures: number;
  credits: number;
  cost_usd: number;
  reviewed: number;
  outcomes: Record<CluChatOutcomeFilter, number>;
  top_blockers: CluChatBlockerCount[];
  review_cost_usd: number;
}

export interface CluChatToolCall {
  name: string;
  status: 'ok' | 'error' | 'pending';
  error: string | null;
}

export interface CluChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string | null;
  created_at: string;
  tools: CluChatToolCall[];
}

export interface CluChatTranscript {
  chat: CluChatRow;
  messages: CluChatMessage[];
}

export interface ListCluChatsArgs {
  limit?: number;
  offset?: number;
  /** Matches org, email or title. */
  q?: string;
  outcome?: CluChatOutcomeFilter | '';
  include_internal?: boolean;
  hours?: number;
  org_id?: string;
}

export interface CluChatSummaryArgs {
  hours?: number;
  include_internal?: boolean;
}

/** The list and summary refresh on this cadence so a live chat shows up
 *  without a reload. Skipped while the tab is unfocused. */
export const CLU_CHATS_POLLING = {
  pollingInterval: 30_000,
  skipPollingIfUnfocused: true,
} as const;

/** Drop unset and blank params so the server applies its own defaults. */
const compact = (
  params: Record<string, string | number | boolean | undefined>
) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== ''
    )
  );

export const adminCluChatsApi = createApi({
  reducerPath: 'adminCluChatsApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    listCluChats: builder.query<CluChatList, ListCluChatsArgs | void>({
      query: (arg) => ({
        url: '/management/clu-chats',
        params: compact({
          limit: arg?.limit,
          offset: arg?.offset,
          q: arg?.q?.trim(),
          outcome: arg?.outcome,
          include_internal: arg?.include_internal,
          hours: arg?.hours,
          org_id: arg?.org_id,
        }),
      }),
    }),
    getCluChatSummary: builder.query<CluChatSummary, CluChatSummaryArgs | void>(
      {
        query: (arg) => ({
          url: '/management/clu-chats/summary',
          params: compact({
            hours: arg?.hours,
            include_internal: arg?.include_internal,
          }),
        }),
      }
    ),
    getCluChat: builder.query<CluChatTranscript, string>({
      query: (conversationId) =>
        `/management/clu-chats/${encodeURIComponent(conversationId)}`,
    }),
  }),
});

export const {
  useListCluChatsQuery,
  useGetCluChatSummaryQuery,
  useGetCluChatQuery,
} = adminCluChatsApi;
